import React, { useState, useEffect } from 'react';
import { Plus, Calendar } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/AuthContext';
import { useSocietyFeatures } from '../../context/SocietyFeaturesContext';
import { events as api, type SocietyEvent, type ApiError } from '../../services/api';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

type RsvpStatus = 'going' | 'not_going' | 'maybe';
const RSVP_LABELS: Record<RsvpStatus, string> = { going: 'Going', maybe: 'Maybe', not_going: 'Not Going' };
const RSVP_BG: Record<RsvpStatus, string> = {
  going: 'rgba(16,185,129,0.12)',
  maybe: 'rgba(245,158,11,0.12)',
  not_going: 'rgba(239,68,68,0.12)',
};
const RSVP_COLOR: Record<RsvpStatus, string> = { going: '#10B981', maybe: '#D97706', not_going: '#DC2626' };

const INIT = { title: '', description: '', venue: '', startsAt: '', endsAt: '', maxAttendees: '' };

const EventsPage: React.FC = () => {
  const { user } = useAuth();
  const { features } = useSocietyFeatures();
  const isResident = user?.role === 'RESIDENT';
  const societyId = user?.societyId ?? null;
  const canManage =
    user?.role === 'SOCIETY_ADMIN' ||
    user?.role === 'STAFF' ||
    (isResident && features.eventsEnabled);

  const [items, setItems] = useState<SocietyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(INIT);
  const [rsvpMap, setRsvpMap] = useState<Record<string, RsvpStatus>>({});
  const [rsvpLoading, setRsvpLoading] = useState<string | null>(null);

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError('');
    try {
      const created = await api.create(societyId, {
        title: form.title,
        startsAt: new Date(form.startsAt).toISOString(),
        description: form.description || undefined,
        venue: form.venue || undefined,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : undefined,
        maxAttendees: form.maxAttendees ? Number(form.maxAttendees) : undefined,
      });
      setItems(p => [created, ...p]);
      setShowForm(false); setForm(INIT);
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to create event');
    } finally { setSaving(false); }
  };

  const handleRsvp = async (eventId: string, status: RsvpStatus) => {
    if (!societyId) return;
    setRsvpLoading(eventId);
    try {
      await api.rsvp(societyId, eventId, status);
      setRsvpMap(p => ({ ...p, [eventId]: status }));
    } catch { /* ignore */ }
    finally { setRsvpLoading(null); }
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const isUpcoming = (iso: string) => new Date(iso) > new Date();

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Events
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
            {items.filter(ev => isUpcoming(ev.startsAt)).length} upcoming · {items.length} total
          </p>
        </div>
        {canManage && (
          <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Create Event
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="nx-card" style={{ padding: 60, textAlign: 'center' }}>
          <Calendar size={32} style={{ color: 'var(--nx-text-muted)', display: 'block', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14, color: 'var(--nx-text-muted)' }}>No events scheduled yet.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {items.map(ev => {
            const upcoming = isUpcoming(ev.startsAt);
            const myRsvp = rsvpMap[ev.id];
            return (
              <div key={ev.id} className="nx-card" style={{ padding: '18px 22px', borderTop: `3px solid ${upcoming ? 'var(--nx-blue)' : 'var(--nx-border)'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', flex: 1, marginRight: 8 }}>
                    {ev.title}
                  </h3>
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600, flexShrink: 0, background: upcoming ? 'rgba(26,108,246,0.1)' : 'rgba(148,163,184,0.1)', color: upcoming ? '#1A6CF6' : '#64748B' }}>
                    {upcoming ? 'Upcoming' : 'Past'}
                  </span>
                </div>
                {ev.description && (
                  <p style={{ fontSize: 12, color: 'var(--nx-text-secondary)', marginBottom: 10, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {ev.description}
                  </p>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--nx-text-muted)' }}>
                  <div><strong style={{ color: 'var(--nx-text-secondary)' }}>Starts:</strong> {fmt(ev.startsAt)}</div>
                  {ev.endsAt && <div><strong style={{ color: 'var(--nx-text-secondary)' }}>Ends:</strong> {fmt(ev.endsAt)}</div>}
                  {ev.venue && <div><strong style={{ color: 'var(--nx-text-secondary)' }}>Venue:</strong> {ev.venue}</div>}
                  {ev.maxAttendees && <div><strong style={{ color: 'var(--nx-text-secondary)' }}>Max:</strong> {ev.maxAttendees} attendees</div>}
                </div>

                {isResident && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--nx-border)' }}>
                    {(['going', 'maybe', 'not_going'] as const).map(s => (
                      <button
                        key={s}
                        disabled={rsvpLoading === ev.id}
                        onClick={() => handleRsvp(ev.id, s)}
                        style={{
                          fontSize: 11, padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
                          background: myRsvp === s ? RSVP_BG[s] : 'transparent',
                          color: myRsvp === s ? RSVP_COLOR[s] : 'var(--nx-text-muted)',
                          border: `1px solid ${myRsvp === s ? RSVP_COLOR[s] : 'var(--nx-border)'}`,
                          opacity: rsvpLoading === ev.id ? 0.5 : 1,
                          transition: 'all 0.15s',
                        }}
                      >
                        {RSVP_LABELS[s]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && canManage && (
        <Modal
          title="Create Event" submitLabel="Create Event" error={formError} isLoading={saving}
          onClose={() => { setShowForm(false); setForm(INIT); setFormError(''); }}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Title *</label>
            <input name="title" value={form.title} onChange={change} required placeholder="Annual Society Day Celebration" style={inp} />
          </div>
          <div>
            <label style={lbl}>Description</label>
            <textarea
              name="description" value={form.description}
              onChange={change} placeholder="Event details and agenda…"
              style={{ ...inp, minHeight: 80, resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={lbl}>Venue</label>
            <input name="venue" value={form.venue} onChange={change} placeholder="Clubhouse, Ground Floor" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Starts At *</label>
              <input type="datetime-local" name="startsAt" value={form.startsAt} onChange={change} required style={inp} />
            </div>
            <div>
              <label style={lbl}>Ends At</label>
              <input type="datetime-local" name="endsAt" value={form.endsAt} onChange={change} style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Max Attendees</label>
            <input type="number" name="maxAttendees" value={form.maxAttendees} onChange={change} placeholder="100" min="1" style={inp} />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default EventsPage;
